<!-- Loading State -->
<div class="dashboard-container" *ngIf="loading">
  <div class="loading-state">
    <div class="loading-spinner"></div>
    <p>Loading dashboard…</p>
  </div>
</div>

<!-- Error State -->
<div class="dashboard-container" *ngIf="!loading && error">
  <div class="error-state">
    <div class="error-icon">⚠️</div>
    <h2>Something went wrong</h2>
    <p>{{ error }}</p>
    <button class="btn-primary" (click)="loadDashboard()">Try Again</button>
  </div>
</div>

<!-- Empty State (new inbound user — no data yet) -->
<div class="dashboard-container" *ngIf="!loading && !error && isEmpty">
  <div class="empty-state">
    <div class="empty-hero">
      <div class="hero-icon">🔍</div>
      <h1>Welcome to Taptimize</h1>
      <p class="hero-subtitle">Find out why competitors outrank you — and what to do about it.</p>
    </div>

    <div class="cta-card">
      <h2>Compare two pages to get started</h2>
      <p>Enter your page and a competitor's page to see a side-by-side analysis of on-page metrics, topic signals, ranked keywords, and more.</p>
      <a routerLink="/page-comparison" class="btn-primary btn-lg">
        Start a Comparison
      </a>
    </div>

    <div class="features-grid">
      <div class="feature-card">
        <div class="feature-icon">📊</div>
        <h3>On-Page Metrics</h3>
        <p>See word count, headings, links, technical scores, and content consistency at a glance.</p>
      </div>
      <div class="feature-card">
        <div class="feature-icon">🏷️</div>
        <h3>Topic Signals</h3>
        <p>Discover which topics and entities each page covers, their weight, and where you have gaps.</p>
      </div>
      <div class="feature-card">
        <div class="feature-icon">🔑</div>
        <h3>Ranked Keywords</h3>
        <p>See which keywords each page ranks for, positions, search volume, and difficulty.</p>
      </div>
    </div>
  </div>
</div>

<!-- Pre-built State (has data — outbound or returning user) -->
<div class="dashboard-container" *ngIf="!loading && !error && !isEmpty && summary">
  <header class="dashboard-header">
    <h1>Dashboard</h1>
    <p class="header-subtitle">Your SEO analysis overview</p>
  </header>

  <!-- Quick Stats -->
  <section class="stats-row">
    <div class="stat-card">
      <div class="stat-value">{{ summary.totalAnalyses }}</div>
      <div class="stat-label">Pages Analyzed</div>
    </div>
    <div class="stat-card">
      <div class="stat-value">{{ summary.recentComparisons.length }}</div>
      <div class="stat-label">Recent Comparisons</div>
    </div>
    <div class="stat-card">
      <div class="stat-value">{{ summary.domains.length }}</div>
      <div class="stat-label">Domains</div>
    </div>
  </section>

  <!-- Quick Actions -->
  <section class="quick-actions">
    <a routerLink="/page-comparison" class="action-card">
      <span class="action-icon">🔍</span>
      <span class="action-text">New Comparison</span>
    </a>
    <a routerLink="/inventory" class="action-card">
      <span class="action-icon">📋</span>
      <span class="action-text">Content Inventory</span>
    </a>
    <a *ngIf="featureFlags.isEnabled('domainTopics') && summary.hasDomainData"
       routerLink="/domain-topics" class="action-card">
      <span class="action-icon">🗺️</span>
      <span class="action-text">Domain Topics</span>
    </a>
  </section>

  <!-- Domain Overview (if outbound / has domain data) -->
  <section class="section" *ngIf="summary.hasDomainData">
    <h2 class="section-title">Domain Overview</h2>
    <div class="domain-cards">
      <div class="domain-card" *ngFor="let d of summary.domains">
        <div class="domain-card-header">
          <span class="domain-name">{{ d.domain }}</span>
        </div>
        <div class="domain-card-body">
          <div class="domain-metric">
            <span class="metric-label">Topics</span>
            <span class="metric-value">{{ d.topicCount }}</span>
          </div>
          <div class="domain-metric">
            <span class="metric-label">Pages</span>
            <span class="metric-value">{{ d.totalPages }}</span>
          </div>
          <div class="domain-metric">
            <span class="metric-label">Owned Topics</span>
            <span class="metric-value">{{ d.ownedTopics }}</span>
          </div>
          <div class="domain-metric">
            <span class="metric-label">Top Authority</span>
            <span class="metric-value" [ngClass]="authorityClass(d.maxAuthority)">{{ d.maxAuthority }}</span>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- Recent Comparisons -->
  <section class="section" *ngIf="summary.hasComparisons">
    <h2 class="section-title">Recent Comparisons</h2>
    <div class="comparisons-table">
      <div class="comparison-row" *ngFor="let c of summary.recentComparisons">
        <div class="comparison-urls">
          <span class="url-label your-page" title="{{ c.yourPageUrl }}">{{ formatUrl(c.yourPageUrl) }}</span>
          <span class="vs-label">vs</span>
          <span class="url-label competitor-page" title="{{ c.competitorPageUrl }}">{{ formatUrl(c.competitorPageUrl) }}</span>
        </div>
        <div class="comparison-meta">
          <span class="badge" [ngClass]="statusClass(c.status)">{{ c.status }}</span>
          <span class="comparison-time">{{ timeAgo(c.startedAt) }}</span>
        </div>
      </div>
    </div>
  </section>

  <!-- CTA if no comparisons yet but has domain data -->
  <section class="section" *ngIf="!summary.hasComparisons">
    <div class="cta-card cta-card-compact">
      <h2>Run your first comparison</h2>
      <p>Pick one of your pages and a competitor to see a detailed side-by-side analysis.</p>
      <a routerLink="/page-comparison" class="btn-primary">Start a Comparison</a>
    </div>
  </section>
</div>
